/* VoiceFillModal — diktování klientských údajů s AI auto-fill.
 *
 * Flow:
 *  1) User klikne na mic → modal s helper otázkami a velkým rec tlačítkem
 *  2) Recording (max 60s) → POST /api/voice-notes/extract-client-fields
 *  3) Backend Whisper → Claude vrátí structured ContactInput patches
 *  4) Modal ukáže review: které pole se vyplní (8 polí z hlasovky) + Apply/Cancel
 *  5) Apply → onApply(patch) → parent ukládá přes PUT /api/crm/contacts/{id}
 */
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, X, CheckCircle2, Loader2, Sparkles, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import InfoTip from "@/components/ui/InfoTip";

import { TL } from "@/lib/prekladyUI";
const HELPER_QUESTIONS = [
  "Oslovení (Pan / Paní / Vážený pane)",
  "IČO + DIČ",
  "Bankovní spojení",
  "Datová schránka",
  "Právní forma (s.r.o., a.s., OSVČ…)",
  "Narozeniny / výročí",
  "Adresa: ulice + PSČ + město + kraj",
  "Web (WWW)",
  "Roční obrat, počet zaměstnanců",
  "Volné poznámky — vše ostatní",
];

const FIELD_LABEL = {
  name: "Jméno", surname: "Příjmení", salutation: "Oslovení",
  phone: "Telefon", email: "E-mail",
  ico: "IČO", dic: "DIČ", legal_form: "Právní forma", company: "Firma",
  bank_account: "Bankovní spojení", data_box: "Datová schránka",
  payment_terms: "Platební podmínky", vat_payer: "Plátce DPH",
  revenue: "Roční obrat", employees: "Zaměstnanců",
  anniversary: "Výročí / Narozeniny", dob: "Datum narození",
  www: "Web",
  street: "Ulice + č.p.", psc: "PSČ", city: "Město", region: "Kraj",
  classification_1: "Klasifikace 1", classification_2: "Klasifikace 2", classification_3: "Klasifikace 3",
  notes: "Poznámka",
};

export default function VoiceFillModal({ open, onClose, onApply }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [extracted, setExtracted] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      stopRecording();
      setTranscript("");
      setExtracted(null);
      setElapsed(0);
    }
  }, [open]); // eslint-disable-line

  const startRecording = async () => {
    setTranscript("");
    setExtracted(null);
    setElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1500) {
          toast.error("Nahrávka je příliš krátká.");
          return;
        }
        await uploadAndExtract(blob);
      };
      rec.start();
      setRecording(true);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e >= 60) {
            stopRecording();
            return 60;
          }
          return e + 1;
        });
      }, 1000);
    } catch {
      toast.error("Nepodařilo se získat přístup k mikrofonu.");
    }
  };

  const stopRecording = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      recRef.current.stop();
    }
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const uploadAndExtract = async (blob) => {
    setProcessing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "client-fields.webm");
      const { data } = await api.post("/api/voice-notes/extract-client-fields", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscript(data.transcript || "");
      setExtracted(data.extracted || {});
      if (data.warning) toast.error(data.warning);
      else if (Object.keys(data.extracted || {}).length === 0) {
        toast.error("AI nenašla žádná data — zkus to znovu jasněji.");
      } else {
        toast.success(`Nalezeno ${data.filled_count} polí.`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI extrakce selhala.";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const apply = () => {
    if (!extracted) return;
    onApply(extracted);
    onClose();
    toast.success(`Vyplněno ${Object.keys(extracted).length} polí z hlasovky.`);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.45)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", bounce: 0.18, duration: 0.45 }}
          onClick={(e) => e.stopPropagation()}
          className="rounded-2xl overflow-hidden w-full max-w-2xl flex flex-col"
          style={{
            background: "var(--bg)",
            boxShadow: "0 30px 80px rgba(80,50,30,0.30), 0 8px 24px rgba(80,50,30,0.15)",
            maxHeight: "92vh",
          }}
          data-testid="voice-fill-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--accent-tint)", color: "var(--accent)" }}>
                <Sparkles size={16} />
              </div>
              <div>
                <div className="text-[15px] font-bold tracking-tight" style={{ color: "var(--ink)" }}>
                  AI Voice-Fill
                </div>
              </div>
            </div>
            <button aria-label={TL("Zavřít")}
              onClick={onClose}
              data-testid="voice-fill-close"
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-[#FFF3EA]"
              style={{ color: "var(--ink-muted)" }}
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-6 py-5 flex-1 min-h-0 overflow-y-auto" data-testid="voice-fill-body">
            {/* PHASE 1 — Idle / Helper questions */}
            {!recording && !processing && !extracted && (
              <>
                <div className="mb-4">
                  <div className="text-[12px] font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--ink-muted)", letterSpacing: "0.06em" }}>
                    Co můžeš diktovat (vše není povinné)
                    {" "}<InfoTip nazev="Diktování" text={'Mluv přirozeně, max. 60 vteřin. Příklad: „Klient se jmenuje Daniel Dač, oslovení pan, IČO 45274649, bankovní spojení 123456789/0100, narozeniny 12. dubna 1985, sídlo Jeseniova 56 Praha 3 PSČ 13000, plátce DPH, roční obrat 12 milionů.“'} />
                  </div>
                  <ul className="space-y-1.5">
                    {HELPER_QUESTIONS.map((q, i) => (
                      <li key={i} className="text-maly flex items-start gap-2" style={{ color: "var(--ink)" }}>
                        <span className="mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }}>•</span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>


                <button
                  onClick={startRecording}
                  data-testid="voice-fill-start"
                  className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all hover:translate-y-[-1px]"
                  style={{
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    color: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(194,89,52,0.30)",
                  }}
                >
                  <Mic size={18} />
                  Spustit nahrávání
                </button>
              </>
            )}

            {/* PHASE 2 — Recording */}
            {recording && (
              <div className="py-6 text-center">
                <div className="relative inline-block mb-4">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
                    style={{
                      background: "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                      boxShadow: "0 8px 28px rgba(220,38,38,0.45)",
                    }}
                  >
                    <Volume2 size={28} color="#FFFFFF" />
                  </div>
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: "rgba(220,38,38,0.55)",
                      animation: "voice-pulse 1.2s ease-out infinite",
                    }}
                  />
                </div>
                <div className="text-[15px] font-bold mb-1" style={{ color: "var(--ink)" }}>
                  Nahrávám…
                </div>
                <div className="text-[28px] font-bold mono tabular-nums mb-3" style={{ color: "#DC2626" }}>
                  0:{String(elapsed).padStart(2, "0")}
                </div>
                <button
                  onClick={stopRecording}
                  data-testid="voice-fill-stop"
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 mx-auto"
                  style={{ background: "#DC2626", color: "#FFFFFF", boxShadow: "0 2px 8px rgba(220,38,38,0.30)" }}
                >
                  <Square size={13} fill="#FFFFFF" /> Stop
                </button>
              </div>
            )}

            {/* PHASE 3 — Processing */}
            {processing && (
              <div className="py-10 text-center">
                <Loader2 size={32} className="animate-spin mx-auto mb-3" style={{ color: "var(--accent)" }} />
                <div className="text-[14px] font-semibold" style={{ color: "var(--ink)" }}>Zpracovávám hlasovku…</div>
                <div className="text-drobny mt-1" style={{ color: "var(--ink-muted)" }}>
                  Whisper → AI extrakce polí
                </div>
              </div>
            )}

            {/* PHASE 4 — Review */}
            {!recording && !processing && extracted && Object.keys(extracted).length > 0 && (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <CheckCircle2 size={18} style={{ color: "#22865A" }} />
                  <div className="text-[14px] font-bold" style={{ color: "var(--ink)" }}>
                    Našel jsem {Object.keys(extracted).length} polí
                  </div>
                </div>

                {transcript && (
                  <details className="mb-3 rounded-lg p-3" style={{ background: "var(--accent-tint)", border: "1px solid var(--border)" }}>
                    <summary className="cursor-pointer text-drobny font-semibold" style={{ color: "var(--accent)" }}>
                      Zobrazit přepis ({transcript.length} znaků)
                    </summary>
                    <div className="text-[12px] mt-2 leading-relaxed" style={{ color: "var(--ink)" }}>
                      „{transcript}"
                    </div>
                  </details>
                )}

                <div
                  className="rounded-xl overflow-hidden mb-4"
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--elite-shadow)" }}
                  data-testid="voice-fill-review"
                >
                  {Object.entries(extracted).map(([k, v], i, arr) => (
                    <div
                      key={k}
                      className="flex items-start gap-3 px-4 py-2.5"
                      style={{ borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}
                    >
                      <div
                        className="text-popisek font-bold uppercase tracking-wide flex-shrink-0 w-32 pt-0.5"
                        style={{ color: "var(--accent)", letterSpacing: "0.04em" }}
                      >
                        {FIELD_LABEL[k] || k}
                      </div>
                      <div className="text-[13px] flex-1 min-w-0 break-words" style={{ color: "var(--ink)" }}>
                        {typeof v === "boolean" ? (v ? "Ano" : "Ne") : String(v)}
                      </div>
                    </div>
                  ))}
                </div>

              </>
            )}
          </div>

          {/* PATIČKA — „Vyplnit do karty" musí být vidět bez rolování */}
          {!recording && !processing && extracted && Object.keys(extracted).length > 0 && (
                <div className="flex gap-2 px-6 py-4 flex-shrink-0" data-testid="voice-fill-actions"
                  style={{ borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
                  <button
                    onClick={() => { setExtracted(null); setTranscript(""); }}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                    style={{ background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)" }}
                    data-testid="voice-fill-redo"
                  >
                    Nahrát znovu
                  </button>
                  <button
                    onClick={apply}
                    className="flex-[2] py-2.5 rounded-lg text-sm font-bold transition-all hover:translate-y-[-1px]"
                    style={{
                      background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                      color: "#FFFFFF",
                      boxShadow: "0 4px 12px rgba(194,89,52,0.30)",
                    }}
                    data-testid="voice-fill-apply"
                  >
                    Vyplnit do karty
                  </button>
                </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
